package net.oc_soft.mswp.ui.grid

import kotlin.math.*
import net.oc_soft.mswp.ui.*
import net.oc_soft.mswp.CellIndex
import net.oc_soft.mswp.ColorScheme
import org.khronos.webgl.*
import net.oc_soft.mswp.Logic
import org.w3c.dom.*


class Buttons(var mineButton : MineButton,
    var rowCount: Int,
    var columnCount: Int,
    var buttonGap : FloatArray,
    var buttonZGap: FloatArray,
    val colorMap : ColorMap) {

    /**
     * indices for each corner button
     */
    val indicesForEachCorner : Array<IntArray>
        get() = arrayOf( 
                intArrayOf(0, 0),
                intArrayOf(columnCount - 1, 0),
                intArrayOf(columnCount - 1, rowCount - 1),
                intArrayOf(0, rowCount - 1)
            ) 
        

    /**
     * cached color for picking
     */
    var colorsForPickingCache : FloatArray? = null
    /**
     * color for picking
     */
    val colorsForPicking : FloatArray
        get() {
            if (colorsForPickingCache == null) {
                registerPickingInfoIntoColorMap()
            }
            return colorsForPickingCache!!
        }
       

    /**
     * button size
     */
    val totalButtonSize : FloatArray
        get() {
            val buttonSize = mineButton.buttonSize
            return floatArrayOf(
                buttonSize[0] * columnCount,
                buttonSize[1] * rowCount)
        }
    /**
     * total gap size
     */
    val totalGapSize : FloatArray
        get() {
            val totalButtonSize = this.totalButtonSize
            val columnRows = arrayOf(columnCount, rowCount)
            return totalButtonSize.mapIndexed({
                i, value ->
                    value * buttonGap[i] * (columnRows[i] - 1)
            }).toFloatArray()
        }
 
    /**
     * gap for drawing
     */
    val gapForDrawing : FloatArray
        get() {
            val totalButtonSize = this.totalButtonSize
            return totalButtonSize.mapIndexed(
                { i, value -> buttonGap[i] * value }).toFloatArray()
        } 
    /**
     * button z gap for drawing
     */ 
    val zGapForDrawing : FloatArray
        get() {
            val totalButtonSize = this.totalButtonSize
            val maxSize = totalButtonSize.maxOrNull() as Float
            return buttonZGap.map({ it * maxSize }).toFloatArray()
        }

    /**
     * textures
     */
    var textures: Textures? = null

    /**
     * logic
     */
    var logic: Logic? = null

    /**
     * transparent texture
     */
    val transparentTexture: WebGLTexture?
        get() {
            return getTransparentTexture()
        }

    /**
     * update color scheme
     */
    fun updateColorScheme(colorScheme: ColorScheme) {
        mineButton.updateColorScheme(colorScheme)
    }

    /**
     * get picking color at row and column.
     */
    fun getPickingColor(rowIndex : Int, colIndex : Int): FloatArray {
        val colors = colorsForPicking
        val baseIndex = (rowIndex * columnCount + colIndex) * 4
        return FloatArray(4) { i -> colors[baseIndex + i] }
    } 

    /**
     * find position by color
     */
    fun findPositionByPickingColor(color : FloatArray) : IntArray? {
        return colorMap.getValue(color) as IntArray?
    }
    /**
     * find position by color
     */
    fun findPositionByPickingColor(color : ByteArray) : IntArray? {
        return colorMap.getValueBy255(color) as IntArray?
    }
 
    fun findPositionByPickingColor(color : Short) : IntArray? {
        return colorMap.getValue(color) as IntArray?
    }
    /**
     * register picking (row and column) into color map
     */
    fun registerPickingInfoIntoColorMap() {
        val maxIndex = min(ColorMap.ButtonsColorRangeIndices[1],
            rowCount * columnCount + ColorMap.ButtonsColorRangeIndices[0])
        val colors = FloatArray((maxIndex
            - ColorMap.ButtonsColorRangeIndices[0]) * 4)
        for (i in ColorMap.ButtonsColorRangeIndices[0]..maxIndex) {
            val baseIndex = i -  ColorMap.ButtonsColorRangeIndices[0]
            val colIndex = baseIndex % columnCount 
            val rowIndex = baseIndex / columnCount
            val color = ColorMap.colorIndexToColor(i)
            colorMap.register(color, intArrayOf(rowIndex, colIndex)) 
            color.forEachIndexed({ j, elem ->
                colors[baseIndex * 4 + j] = elem
            }) 
        } 
        colorsForPickingCache = colors
    }
    
    /**
     * get texture instance for button
     */
    fun getNumberImage(rowIndex: Int, colIndex: Int): WebGLTexture? {
        var result : WebGLTexture? = null
        var logic = this.logic
        var textures = this.textures
        if (textures != null && logic != null) {
            val num = logic.getNumberIfOpened(rowIndex, colIndex)  
            val locked = logic.isLocking(rowIndex, colIndex)
            if (num != null && num > 0) {
                if (locked != null && locked) {
                    result = textures.getNumberFlagTexture(num)
                } else {
                    result = textures.getNumberBlankTexture(num)
                }
            }  
        }
        return result
    }
    /**
     * get alternate texture
     */
    fun getAlternateTexture(rowIndex: Int, colIndex: Int) : WebGLTexture? {
        var result : WebGLTexture? = null
        var logic = this.logic
        if (logic != null) {
            val cell = CellIndex(rowIndex, colIndex)
            val locked = logic.isLocking(rowIndex, colIndex)
            if (logic.isOver && cell in logic.mineLocations) {
                if (locked != null && locked) {
                    result = getNgFlagTexture()
                } else {
                    result = getNgTexture() 
                }
            } else {
                if (locked != null && locked) {
                    result = getOkFlagTexture()
                } else {
                    result = getOkTexture()
                }
            }
        }
        return result
    }
       
    /**
     * you get true if the location is hint to open without fail.
     */
    fun isHintLocation(
        rowIndex: Int,
        colIndex: Int): Boolean {
        var result = false
        val logic = this.logic 
        if (logic != null) {
            val status = logic.status 
            if (status != null) {
                if (!status.isStarted) {
                    val hintLoc = logic.hintToStart
                    if (hintLoc != null) {
                        result = hintLoc == CellIndex(rowIndex, colIndex)
                    }
                } 
            }
        }
        return result
    }
    /**
     * you get true if the location is opening.
     */
    fun isUserInputingLocation(
        rowIndex: Int,
        colIndex: Int): Boolean {
        var result = false
        val logic = this.logic 
        if (logic != null) {
            val status = logic.status 
            if (status != null) {
                val userInputLoc = status.userInputingButton 
                if (userInputLoc != null) {
                    result = userInputLoc == CellIndex(rowIndex, colIndex)
                }
            }
        }
        return result
    }
    

    /**
     * get transparent texture
     */
    fun getTransparentTexture() : WebGLTexture? {
        var textures = this.textures
        var result : WebGLTexture? = null
        if (textures != null) {
            result = textures.blackTransparentTexture 
        }
        return result
    }

    /**
     * get ng texture
     */
    fun getNgTexture() : WebGLTexture? {
        var textures = this.textures
        var result : WebGLTexture? = null
        if (textures != null) {
            result = textures.ngTexture 
        }
        return result
    }

    /**
     * get ok texture
     */
    fun getOkTexture() : WebGLTexture? {
        var textures = this.textures
        var result : WebGLTexture? = null
        if (textures != null) {
            result = textures.okTexture 
        }
        return result
    }
    /**
     * get ng-flag texture
     */
    fun getNgFlagTexture() : WebGLTexture? {
        var textures = this.textures
        var result : WebGLTexture? = null
        if (textures != null) {
            result = textures.ngFlagTexture 
        }
        return result
    }

    /**
     * get ok-flag texture
     */
    fun getOkFlagTexture() : WebGLTexture? {
        var textures = this.textures
        var result : WebGLTexture? = null
        if (textures != null) {
            result = textures.okFlagTexture 
        }
        return result
    }
}

// vi: se ts=4 sw=4 et:
